import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, ArrowRight, ArrowLeft, CheckCircle2, Copy, Sparkles } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import Logo from "../components/Logo";

/**
 * /forgot-password
 *
 * Step 1 of the password reset flow.
 *  - Asks for an email
 *  - On success, shows a confirmation screen ("check your inbox")
 *  - In MVP / debug mode the backend returns a `debug_link` field so the
 *    field tester can copy the reset link without a real mail provider.
 *    We render that link inside an obvious "MODE TEST" panel.
 */
export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [debugLink, setDebugLink] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-password", {
                email: email.trim(),
            });
            setSubmitted(true);
            setDebugLink(data?.debug_link || "");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                toast.error(formatApiError(err.response?.data?.detail));
            } else {
                toast.error(
                    formatApiError(err.response?.data?.detail) || err.message,
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const copyDebugLink = async () => {
        try {
            await navigator.clipboard.writeText(debugLink);
            toast.success("Lien copié dans le presse-papier");
        } catch {
            toast.error("Impossible de copier — copie-le à la main");
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-shariti-deep-black px-6 py-10 relative overflow-hidden">
            <div className="absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full bg-shariti-orange/25 blur-[120px] animate-blob" />
            <div className="absolute -bottom-40 -right-32 w-[400px] h-[400px] rounded-full bg-shariti-yellow/15 blur-[110px] animate-blob" />

            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-md"
                data-testid="forgot-password-screen"
            >
                <div className="mb-8 flex justify-center">
                    <Logo
                        variant="full"
                        showAcademy
                        academySize="md"
                        className="h-20 w-auto drop-shadow-[0_0_24px_rgba(238,114,46,0.35)]"
                    />
                </div>

                {!submitted ? (
                    <>
                        <p className="text-xs font-thin tracking-[0.3em] text-shariti-orange-light uppercase mb-2 text-center">
                            Mot de passe oublié
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-3 text-center">
                            On te renvoie un accès.
                        </h2>
                        <p className="text-white/55 font-light mb-8 text-center text-balance">
                            Entre l'email associé à ton compte. On t'envoie un lien
                            sécurisé pour choisir un nouveau mot de passe.
                        </p>

                        <form
                            onSubmit={handleSubmit}
                            className="space-y-4"
                            data-testid="forgot-password-form"
                        >
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                <input
                                    type="email"
                                    placeholder="ton@email.fr"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoFocus
                                    data-testid="forgot-email-input"
                                    className="w-full pl-12 pr-4 py-3.5 glass-card rounded-2xl text-white placeholder-white/30 focus:outline-none focus:border-shariti-orange/60 transition-all"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="forgot-submit-button"
                                className="w-full flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-3.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                            >
                                {loading ? "Envoi en cours…" : "Envoyer le lien"}
                                {!loading && <ArrowRight className="w-5 h-5" />}
                            </button>
                        </form>

                        <Link
                            to="/login"
                            data-testid="forgot-back-to-login"
                            className="mt-6 flex items-center justify-center gap-2 text-sm text-white/55 hover:text-white transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Retour à la connexion
                        </Link>
                    </>
                ) : (
                    <div data-testid="forgot-password-success">
                        <div className="mx-auto w-16 h-16 rounded-full bg-shariti-orange/20 border border-shariti-orange/40 flex items-center justify-center mb-6">
                            <CheckCircle2 className="w-9 h-9 text-shariti-orange" />
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-3 text-center">
                            Vérifie ta boîte mail
                        </h2>
                        <p className="text-white/55 font-light mb-6 text-center text-balance">
                            Si un compte existe avec <span className="text-white">{email}</span>,
                            un lien de réinitialisation vient d'être envoyé. Le lien
                            est valable <span className="text-white">60 minutes</span>.
                        </p>

                        {debugLink && (
                            <div
                                className="glass-card rounded-2xl p-4 mb-6 border-shariti-yellow/30 bg-shariti-yellow/5"
                                data-testid="forgot-debug-panel"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Sparkles className="w-4 h-4 text-shariti-yellow shrink-0" />
                                    <p className="text-xs font-bold tracking-widest text-shariti-yellow uppercase">
                                        Mode test
                                    </p>
                                </div>
                                <p className="text-xs text-white/60 mb-3 leading-relaxed">
                                    Aucun service email n'est branché pour le moment.
                                    Copie ce lien manuellement pour finaliser la
                                    réinitialisation.
                                </p>
                                <div
                                    className="flex items-center gap-2 bg-black/40 rounded-xl px-3 py-2 border border-white/5"
                                    data-testid="forgot-debug-link"
                                >
                                    <code className="text-[11px] text-white/80 break-all flex-1 font-mono">
                                        {debugLink}
                                    </code>
                                    <button
                                        type="button"
                                        onClick={copyDebugLink}
                                        className="shrink-0 p-2 rounded-lg bg-shariti-yellow/20 hover:bg-shariti-yellow/30 transition-colors"
                                        data-testid="forgot-debug-copy"
                                        aria-label="Copier le lien"
                                    >
                                        <Copy className="w-4 h-4 text-shariti-yellow" />
                                    </button>
                                </div>
                                <a
                                    href={debugLink}
                                    className="mt-3 inline-flex items-center gap-2 text-xs font-bold text-shariti-orange hover:text-shariti-orange-light transition-colors"
                                    data-testid="forgot-debug-open"
                                >
                                    Ouvrir le lien
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </a>
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={() => {
                                setSubmitted(false);
                                setDebugLink("");
                            }}
                            data-testid="forgot-resend-button"
                            className="w-full glass-card rounded-2xl py-3 mb-3 hover:border-shariti-orange/40 transition-all font-bold text-white/80"
                        >
                            Renvoyer un lien
                        </button>
                        <Link
                            to="/login"
                            data-testid="forgot-success-back-to-login"
                            className="flex items-center justify-center gap-2 text-sm text-white/55 hover:text-white transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Retour à la connexion
                        </Link>
                    </div>
                )}
            </motion.div>
        </div>
    );
}
